import { Paper, Box, Typography, useTheme, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useState, useEffect, useRef, useMemo } from "react";

// Determine step size based on magnitude of number
const getStepSize = (value: number, progress: number): number => {
  const absValue = Math.abs(value);
  
  // Base step size based on magnitude
  let baseStep: number;
  if (absValue >= 100000) baseStep = 1000;
  else if (absValue >= 10000) baseStep = 100;
  else if (absValue >= 1000) baseStep = 10;
  else baseStep = 1;
  
  // Reduce step size as we approach the end for precise landing
  if (progress > 0.95) return 1;
  if (progress > 0.85) return Math.max(1, baseStep / 10);
  if (progress > 0.7) return Math.max(1, baseStep / 5);
  
  return baseStep;
};

// Round to nearest step
const roundToStep = (value: number, step: number): number => {
  return Math.round(value / step) * step;
};

// Custom hook for count-up animation with natural deceleration
const useCountUp = (end: number) => {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  
  // Random duration between 1000-2000ms
  const duration = useMemo(() => 1000 + Math.random() * 800, []);
  
  // Random start delay (0-150ms) so cards don't all start at once
  const startDelay = useMemo(() => Math.random() * 150, []);

  useEffect(() => {
    startTimeRef.current = null;
    
    const timeoutId = setTimeout(() => {
      const animate = (timestamp: number) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }
        
        const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
        
        // Aggressive ease-out: very fast start, crawls at the end
        const eased = 1 - Math.pow(1 - progress, 5);
        
        // Calculate raw value and round to appropriate step
        const rawValue = eased * end;
        const step = getStepSize(end, progress);
        const steppedValue = roundToStep(rawValue, step);
        
        setCount(Math.min(steppedValue, end));
        
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      
      rafRef.current = requestAnimationFrame(animate);
    }, startDelay);
    
    return () => {
      clearTimeout(timeoutId);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [end, duration, startDelay]);

  return count;
};

// Parse a formatted string to number (handles currency symbols, commas, etc.)
const parseFormattedNumber = (value: string | number): number => {
  if (typeof value === "number") return value;
  // Remove currency symbols, commas, percent signs, and parse
  const cleaned = value.replace(/[$€¥£₹₽¢₩₪₨%,\s]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: "primary" | "success" | "warning" | "error" | "info";
  animate?: boolean;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
}

export const StatCard = ({
  title,
  value,
  icon,
  color = "primary",
  animate = true,
  prefix = "",
  suffix = "",
  tooltip,
}: StatCardProps) => {
  const theme = useTheme();
  const paletteColorByVariant = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const numericValue = parseFormattedNumber(value);
  const animatedValue = useCountUp(numericValue);
  
  // Format with commas and apply prefix/suffix
  const displayValue = animate 
    ? `${prefix}${animatedValue.toLocaleString()}${suffix}` 
    : `${prefix}${value}${suffix}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: 3,
        borderColor: (t) => t.palette.divider,
        transition: "transform .25s cubic-bezier(.16,1,.3,1), box-shadow .25s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (t) => t.shadows[3],
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 44,
          borderRadius: 2.5,
          flexShrink: 0,
          color: paletteColorByVariant[color],
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? `${paletteColorByVariant[color]}26`
              : `${paletteColorByVariant[color]}1A`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            letterSpacing: "-0.01em",
            lineHeight: 1.15,
          }}
        >
          {displayValue}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: ".82rem" }}
          >
            {title}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
